import { FunctionComponent, useRef, useState, memo, Suspense, lazy, useLayoutEffect } from 'react'
import { Typography, Box, Avatar, Link, IconButton, makeStyles } from '@material-ui/core'
import { PageviewRounded, Close, Delete, History } from '@material-ui/icons'
import styled from 'styled-components/macro'
//@ts-ignore
import { useTheme } from 'styled-components'
import { ReactComponent as CommentLeft } from 'icons/comment-left.svg'
import { ReactComponent as Share } from 'icons/share-icon.svg'
import { ReactComponent as CopyIcon } from 'icons/copy-link.svg'
import { ReactComponent as EditIcon } from 'icons/edit-icon-small.svg'
import { ReactComponent as DeleteIcon } from 'icons/trash-icon.svg'
import { ReactComponent as ReportIcon } from 'icons/report.svg'
import {
  BackendUser,
  Content,
  CommentPost,
  CommentsList,
  BackendPartner,
  Reaction,
  UserFunction,
  ContentActivity
} from 'common/types'
import moment from 'moment'
import { ElasticSlider } from 'components/core/slider/ElasticSlider'
import { ReactComponent as ContextIcon } from 'icons/more-horizontal.svg'
import { SharedPostCard } from './SharedPostCard'
import { useHistory, useParams } from 'react-router-dom'
import { MainButton, ChildButton, Directions } from 'react-floating-button-menu'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { ReactComponent as ThumbsUpIcon } from 'icons/thumbs-up-blue.svg'
import { ReactComponent as ThumbsUpOutlinedIcon } from 'icons/thumbs-up-outlined-blue.svg'
import { ReactComponent as HeartIcon } from 'icons/heart-red.svg'
import { ReactComponent as HeartOutlinedIcon } from 'icons/heart-outlined-red.svg'
import { ReactComponent as ClapIcon } from 'icons/clap-green.svg'
import { ReactComponent as ClapOutlinedIcon } from 'icons/clap-outlined-green.svg'
import { PostCardReactions } from './post-card-reactions/PostCardReactions'
import { Loader } from 'components/core/loader'
import { usePost } from 'hooks/use-post/usePost'
import { Comments } from '../comments'

//@ts-ignore
import ReactTextFormat from 'react-text-format'
import { StyledCard, StyledMentionsTitle } from './styled'
import { htmlToReactParser } from 'common/HTMLToReactParser'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { getHTMLWithLinkedTags } from 'common/utils/getHTMLWithLinkedTags'
import { PostCardImage } from './PostCardImage'
import { PostCardVideo } from './PostCardVideo'
import { Button } from 'common/atoms/Button/Button'

const LazyFAB = lazy(() => import('react-floating-button-menu').then(module => ({ default: module.FloatingMenu })))
const LazyCreateComment = lazy(() => import('../comments').then(module => ({ default: module.CreateComment })))
const LazyPostCardContextMenu = lazy(() =>
  import('./PostCardContextMenu').then(module => ({ default: module.PostCardContextMenu }))
)
const LazyCommentsCount = lazy(() => import('../comments').then(module => ({ default: module.CommentCount })))

const CommentLeftIcon = styled(CommentLeft)`
  fill: ${props => props.theme.colors.primary};
  stroke: ${props => props.theme.colors.primary};
`

const StarIcon = styled(Close)`
  fill: ${props => props.theme.colors.primary};
  stroke: ${props => props.theme.colors.primary};
`

const ShareIcon = styled(Share)`
  fill: ${props => props.theme.colors.primary};
  stroke: ${props => props.theme.colors.primary};
`

const StyledPostDescription = styled(Typography)`
  display: inline-block;
  max-width: inherit;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  max-height: 2.9em;
  line-height: 1.2em;
  white-space: pre-wrap;
  word-break: break-word;

  a,
  a:link,
  a:visited {
    color: ${props => props.theme.colors.primary} !important;
  }
`
const StyledShowMoreDescription = styled(Typography)`
  display: inline-block;
  max-width: inherit;
  max-height: inherit;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.2em;
  white-space: pre-wrap;
  word-break: break-word;

  a,
  a:link,
  a:visited {
    color: ${props => props.theme.colors.primary} !important;
  }
`

export const StyledContextIconButton = styled(IconButton)`
  && {
    height: 0.5em;
  }
`
export const StyledContextIcon = styled(ContextIcon)`
  && {
    height: 1.3em;
    color: ${props => props.theme.colors.accent1};
  }
`

export const StyledCopyIcon = styled(CopyIcon)<{ isDevice?: boolean }>`
  && {
    fill: ${props => props.theme.colors.accent1};
    width: ${props => (!props.isDevice ? '0.875rem' : '0.8em')};
    height: 0.8em;
  }
`

export const StyledEditIcon = styled(EditIcon)`
  && {
    fill: ${props => props.theme.colors.accent1};
    width: 0.8em;
    height: 0.8em;
  }
`

export const StyledReportIcon = styled(ReportIcon)`
  && {
    fill: ${props => props.theme.colors.accent1};
    width: 0.8em;
    height: 0.8em;
  }
`

export const StyledDeleteIcon = styled(DeleteIcon)`
  && {
    fill: ${props => props.theme.colors.accent1};
    width: 0.8em;
    height: 0.8em;
  }
`
export const StyledViewIcon = styled(PageviewRounded)`
  && {
    fill: ${props => props.theme.colors.accent1};
    width: 0.8em;
    height: 0.8em;
  }
`

export const StyledLargeDeleteIcon = styled(Delete)`
  && {
    fill: ${props => props.theme.colors.accent1};
  }
`

export const StyledHistoryIcon = styled(History)`
  && {
    fill: ${props => props.theme.colors.accent1};
    width: 0.8em;
    height: 0.8em;
    margin-left: -0.1em;
  }
`

interface PostCardProps {
  user?: BackendUser
  deleteContent: Function
  isDevice: boolean
  post: any
  setPosts?: Function
  posts?: Content[]
  allPostsAfterDelete?: Content[]
  setAllPostsAfterDelete?: Function
  fetchComments: Function
  createReaction: Function
  fetchReactionCount: Function
  fetchUserReaction: Function
  createComment: Function
  deleteComment: Function
  createSharePost?: Function
  updateComment: Function
  notify?: Function
  comments?: CommentsList
  reportContent: Function
  hideFeedDetails?: boolean
  hideFeed?: boolean
  handleComment: Function
  showEdit: boolean
  setShowEdit: Function
  showClearModal: boolean
  setShowClearModal: Function
  myPosts: Content[]
  setMyPosts: Function
  setShowLoader: Function
  updateContent: Function
  updateContentActivity: Function
  getPost: Function
  showReportModal: boolean
  setShowReportModal: Function
  setActivePost: Function
  postEdited: boolean
  setShowEditPost: Function
  setOpenDelete: Function
  setOpenSharePostModal: Function
  fetchCommentCount: Function
  setShowUserReactions: Function
  profilePosts?: boolean
  partner?: BackendPartner
  partnerView?: boolean
  backendUserFunctions: UserFunction[]
}

const PostCard: FunctionComponent<PostCardProps> = ({
  post,
  user,
  isDevice,
  fetchComments,
  createReaction,
  createComment,
  deleteComment,
  updateComment,
  notify,
  comments,
  reportContent,
  hideFeedDetails,
  hideFeed,
  handleComment,
  getPost,
  showReportModal,
  setShowReportModal,
  setActivePost,
  postEdited,
  setShowEditPost,
  setOpenDelete,
  setOpenSharePostModal,
  fetchReactionCount,
  fetchCommentCount,
  fetchUserReaction,
  setShowUserReactions,
  profilePosts,
  partner,
  partnerView,
  backendUserFunctions
}) => {
  const theme = useTheme()
  const history = useHistory()
  const { backendUser, backendPartner } = useAuth()
  const videoRef = useRef<HTMLDivElement | null>(null)
  const descriptionRef = useRef<HTMLElement>(null)
  const descriptionRefShared = useRef<HTMLElement>(null)
  const [likes, setLikes] = useState(post?.reactionCount || 0)
  const [showComments, setShowComments] = useState(hideFeed)
  const [commentsFetched, setCommentsFetched] = useState(hideFeed)
  const [commentCount, setCommentCount] = useState(0)
  const [showMore, setShowMore] = useState(false)
  const [page, setPage] = useState(0)
  const [userReaction, setUserReaction] = useState([0, 0, 0])
  const [userReactionAll, setUserReactionAll] = useState([0, 0, 0])
  const [userReacted, setUserReacted] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [postComments, setPostComments] = useState<CommentPost[]>([])
  const createCommentPermission = backendUserFunctions?.find(x => x?.name === 'Create Comment')
  const { userId } = useParams<{ userId: string }>()
  const { partnerId } = useParams<{ partnerId: string }>()
  const { logEventAnalytics } = useAnalytics()
  const { parseContentBody, displayShowMore, displaySharedShowMore, parseSharedContentBody } = usePost()

  const isReported = () => {
    return post?.reportedBy?.some(report => {
      if (report === user?.id) {
        return true
      }
      return false
    })
  }

  const getComments = async (userId: string, postId: string, page: number) => {
    const response: CommentsList = await fetchComments(userId, postId, page)
    const uniqueComment = response?.data.filter(x => {
      const existingComment = postComments?.find(y => y?.id === x?.id)
      if (!existingComment) return x
    })
    if (postId && uniqueComment) {
      setPostComments([...postComments, ...uniqueComment])
    }
    return response
  }

  const displayComments = () => {
    if (postComments?.length > 0) {
      return postComments
    } else if (comments && comments?.data.length > 0) {
      return comments.data
    } else {
      return []
    }
  }

  const showContextMenu = () => {
    if (hideFeed && window.location.href.includes('/feed')) {
      navigator.clipboard.writeText(window.location.href)
    } else if (!hideFeed && window.location.href.includes('/profile')) {
      const postPage = window.location.href.replace('/profile', '/feed').replace(`/${userId}`, '')
      navigator.clipboard.writeText(postPage + '/post/' + post?.id)
    } else {
      const postPage = window.location.href.includes(`/company/${partnerId}`)
        ? window.location.href.replace('/company', '/feed').replace(`/${partnerId}`, '')
        : window.location.href
      navigator.clipboard.writeText(postPage + '/post/' + post?.id)
    }
    notify('success', 'Link copied to clipboard')
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = user?.id
    if (user?.partnerId) {
      partnerLog['Partner ID'] = partner?.id
      partnerLog['Company Name'] = partner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = user?.role
    }
    logEventAnalytics(
      user?.partnerId ? 'Partner' : user?.role,
      'Copy link to post',
      'both',
      'Copy Post link',
      { 'Post ID': post?.id, 'Post author ID': post?.userId, ...partnerLog, ...userLog },
      user?.id
    )
  }

  const getReactionString = (index: number) => {
    switch (index) {
      case 0: {
        return 'like'
      }
      case 1: {
        return 'love'
      }
      case 2: {
        return 'clap'
      }
    }
  }

  const handleReaction = async (index: number) => {
    const newReaction = [0, 0, 0]
    const newLikes = [Number(likes)]
    const newReactionAll = userReactionAll
    const reactionString = getReactionString(index)

    if (userReaction[index] === 1) {
      newReaction[index] = 0
      newLikes[0] = newLikes[0] - 1
      newReactionAll[index] = newReactionAll[index] - 1
    } else {
      newReaction[index] = 1
      userReaction.forEach((reaction, index) => {
        newLikes[0] = reaction === 1 ? newLikes[0] - 1 : newLikes[0]
        newReactionAll[index] = reaction === 1 ? newReactionAll[index] - 1 : newReactionAll[index]
      })
      newReactionAll[index] = newReactionAll[index] + 1
    }
    setUserReaction(newReaction)
    setLikes(newLikes[0] + newReaction[0] + newReaction[1] + newReaction[2])
    setUserReactionAll(newReactionAll)
    setUserReacted(true)
    await createReaction(user?.id, post?.id, reactionString)
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = user?.id
    if (user?.partnerId) {
      partnerLog['Partner ID'] = partner?.id
      partnerLog['Company Name'] = partner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = user?.role
    }
    logEventAnalytics(
      user?.partnerId ? 'Partner' : user?.role,
      'Post liked',
      'both',
      'Post liked',
      { 'Post ID': post?.id, 'Post author ID': post?.userId, ...partnerLog, ...userLog },
      user?.id
    )
  }

  const getExistingUserReaction = async () => {
    const existingUserReaction: ContentActivity[] = await fetchUserReaction(user?.id, post?.id)
    if (existingUserReaction) {
      const reaction: ContentActivity = existingUserReaction.find(x => !x.partnerId)
      const reactionType = reaction?.typeValue
      switch (reactionType) {
        case 'like': {
          setUserReaction([1, 0, 0])
          break
        }
        case 'love': {
          setUserReaction([0, 1, 0])
          break
        }
        case 'clap': {
          setUserReaction([0, 0, 1])
          break
        }
      }
    }
  }

  useLayoutEffect(() => {
    if (post?.id) {
      getReactionCount(post.id)
      getCommentCount(post.id)
      getExistingUserReaction()
    }
  }, [])

  const getCommentCount = async (contentId: string) => {
    const response = await fetchCommentCount(contentId)
    if (response) {
      setCommentCount(response)
    }
  }
  const getReactionCount = async (contentId: string) => {
    const receivedReactions: Reaction[] = await fetchReactionCount(contentId)
    const newUserReactionAll = [0, 0, 0]
    receivedReactions?.map(reactionObj => {
      if (reactionObj.reaction === 'like') {
        newUserReactionAll[0] = reactionObj.count
      } else if (reactionObj.reaction === 'love') {
        newUserReactionAll[1] = reactionObj.count
      } else if (reactionObj.reaction === 'clap') {
        newUserReactionAll[2] = reactionObj.count
      }
    })
    setUserReactionAll(newUserReactionAll)
  }

  const handleScroll = async e => {
    //This is a stop gap for the release
    let bottom = e.target.scrollHeight - e.target.scrollTop + 2 >= e.target.clientHeight
    if (bottom) {
      const userComments: CommentsList = await getComments(user?.id, post?.id, bottom ? page + 1 : page)
      if (Number(userComments?.totalCount) > postComments?.length) {
        setPage(Number(userComments?.page))
        setPostComments([...postComments, ...userComments?.data])
      }
    }
  }

  const useStyles = makeStyles({
    root: {
      width: '100%',
      padding: '0',
      borderRadius: '15px',
      backgroundColor: profilePosts ? theme.colors.bgPrimary : theme.colors.supplementary,
      marginTop: `${comments?.data?.length ? '0em' : 'none'}`,
      border: profilePosts ? `none` : 'unset',
      boxShadow: profilePosts ? 'none' : 'default'
    },
    media: {
      height: 100,
      width: 100,
      justifyContent: 'left',
      borderRadius: '10px',
      marginRight: 1
    },
    content: {
      padding: 0
    },
    actions: {
      justifyContent: 'center'
    },
    disabled: {
      cursor: 'default'
    },
    clickable: {
      color: theme.colors.primary
    }
  })
  const classes = useStyles()

  const customLinkDecorator = (decoratedHref: string, decoratedText: string, linkTarget: string, key: number) => (
    <a href={decoratedHref} key={key} target="_blank" rel="noopener" className={classes.clickable}>
      {decoratedText}
    </a>
  )

  const getName = (): string => {
    if (post?.companyName) {
      return post.companyName
    } else {
      return `${post.firstName} ${post.lastName}`
    }
  }

  const postCreatorLink = () => {
    if (post?.partnerId) {
      history.push(`/company/${post?.partnerId}`)
    } else {
      user?.id === post?.userId ? history.push(`/profile/`) : history.push(`/profile/${post?.userId}`)
    }

    const partnerLog = {}
    const userLog = {}
    if (user?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = user?.role
    }

    logEventAnalytics(
      user?.partnerId ? 'Partner' : user?.role,
      'Profile viewed',
      'mixpanel',
      'Profile viewed',
      {
        'Member profile viewed': post?.userId,
        'User ID': user?.id,
        'From post': true,
        'From chat': false,
        'From managed connections': false,
        'From pings': false,
        'From suggested connections': false,
        ...partnerLog,
        ...userLog
      },
      user?.id,
      {
        $name: `${user?.firstName} ${user?.lastName}`,
        $email: user?.username
      }
    )
  }

  const handleOpenSharePost = () => {
    if (isDevice && partnerView) {
      history.push(`/partner/post/share/${post?.id}`)
    } else if (isDevice) {
      history.push(`/post/share/${post?.id}`)
    } else {
      setOpenSharePostModal(true)
      setActivePost(post)
    }
  }

  const videoId = `${post?.id}-video`

  const onVideoPlay = () => {
    const activeVideo = event?.target as HTMLVideoElement

    document.querySelectorAll('video').forEach(video => {
      if (video && video != activeVideo) {
        video?.pause()
      }
    })
  }

  const linkToOwnProfile = (html: string) => {
    return html?.replaceAll(`/profile/${backendUser?.id}`, '/profile/') || html
  }

  const addLinks = (content: string) => linkToOwnProfile(getHTMLWithLinkedTags(content))

  const contentBody = addLinks(parseContentBody(post?.content))
  const contentSharedBody = addLinks(parseSharedContentBody(post?.content[0]?.comment))

  return (
    <StyledCard className={classes.root}>
      <Box display="flex" justifyContent="space-between" p="0.5em">
        <Box display="flex" flexDirection="row">
          <Box
            onClick={() => postCreatorLink()}
            marginRight="1.5em"
            marginBottom={isDevice ? '0.8em' : 0}
            style={{ cursor: 'pointer' }}
          >
            <Avatar
              src={post?.partnerId && post?.logo ? post?.logo : post?.image}
              alt="member with a profile"
              style={{ width: '2em', height: '2em' }}
            />
          </Box>
          <Box>
            <Box onClick={() => postCreatorLink()} style={{ cursor: 'pointer' }}>
              <Typography component="span" variant="subtitle2" align="left">
                <Box color={theme.colors.accent1}>{getName()}</Box>
              </Typography>
              {!post?.partnerId && (
                <Box color={theme.colors.accent}>
                  <Typography component="span" variant="body2" style={{ wordWrap: 'break-word' }}>
                    <Box color={theme.colors.accent1} display="inline">
                      {post?.connectionTitle?.slice(0, 45)}
                    </Box>
                  </Typography>
                  {post?.connectionTitle?.length > 45 && (
                    <Typography variant="subtitle2" style={{ display: 'contents' }}>
                      ...
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Box>
              <Typography component="span" variant="caption">
                <Box color={theme.colors.accent}>
                  {moment(post?.createdAt).fromNow() === moment(post?.updatedAt).fromNow()
                    ? `Posted ${moment(post?.createdAt).fromNow()}`
                    : `Posted ${moment(post?.createdAt).fromNow()} - Last edit ${moment(post?.updatedAt).fromNow()}`}
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>
        <Suspense fallback={''}>
          <LazyPostCardContextMenu
            setActivePost={setActivePost}
            post={post}
            user={user}
            hideFeedDetails={hideFeedDetails}
            setShowEditPost={setShowEditPost}
            setOpenDelete={setOpenDelete}
            isReported={isReported()}
            showContextMenu={showContextMenu}
            setShowReportModal={setShowReportModal}
            showReportModal={showReportModal}
            reportContent={reportContent}
            notify={notify}
            backendUserFunctions={backendUserFunctions}
          />
        </Suspense>
      </Box>
      <Box p="0 0.5em">
        <Box display="flex" flexDirection="column" color={theme.colors.accent1}>
          {Array.isArray(post?.content) && (
            <>
              <Box display="flex" gridGap="0.5em">
                <Box marginBottom="0.5rem">
                  {!showMore && (
                    <StyledPostDescription variant="body2" align="justify" ref={descriptionRefShared}>
                      <ReactTextFormat allowedFormats={['URL']} linkDecorator={customLinkDecorator}>
                        {htmlToReactParser.parse(contentSharedBody)}
                      </ReactTextFormat>
                    </StyledPostDescription>
                  )}
                  {displaySharedShowMore(post?.content[0].comment, descriptionRefShared) && !showMore && (
                    <Link onClick={() => setShowMore(true)}>
                      <Typography component="span" variant="subtitle2">
                        <Box color={theme.colors.primary}>See More</Box>
                      </Typography>
                    </Link>
                  )}
                  {showMore && (
                    <StyledShowMoreDescription variant="body2" align="justify" ref={descriptionRefShared}>
                      <ReactTextFormat allowedFormats={['URL']} linkDecorator={customLinkDecorator}>
                        {htmlToReactParser.parse(contentSharedBody)}
                      </ReactTextFormat>
                    </StyledShowMoreDescription>
                  )}
                </Box>
              </Box>
              <Box>
                <SharedPostCard
                  history={history}
                  post={post?.content}
                  theme={theme}
                  userId={user?.id}
                  isDevice={isDevice}
                  postEdited={postEdited}
                  parseContentBody={parseContentBody}
                  displayShowMore={displayShowMore}
                  profilePosts={profilePosts}
                />
              </Box>
            </>
          )}
          {!Array.isArray(post?.content) && (
            <>
              <Typography gutterBottom variant="body2" component="span">
                <span></span>
                <StyledMentionsTitle fontWeight="700">
                  {htmlToReactParser.parse(post?.contentTitle)}
                </StyledMentionsTitle>
              </Typography>
              <Box display="flex" gridGap="0.5em" justifyContent="space-between">
                <Box marginBottom="0.5rem">
                  {!showMore && (
                    <StyledPostDescription variant="body2" align="justify" ref={descriptionRef}>
                      <ReactTextFormat allowedFormats={['URL']} linkDecorator={customLinkDecorator}>
                        {htmlToReactParser.parse(contentBody)}
                      </ReactTextFormat>
                    </StyledPostDescription>
                  )}

                  {showMore && (
                    <StyledShowMoreDescription variant="body2" align="justify">
                      <ReactTextFormat allowedFormats={['URL']} linkDecorator={customLinkDecorator}>
                        {htmlToReactParser.parse(contentBody)}
                      </ReactTextFormat>
                    </StyledShowMoreDescription>
                  )}

                  {displayShowMore(post?.content, descriptionRef) && !showMore && (
                    <Link onClick={() => setShowMore(true)}>
                      <Typography component="span" variant="subtitle2">
                        <Box color={theme.colors.primary}>See More</Box>
                      </Typography>
                    </Link>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box>
        {Array.isArray(post?.media) && post?.media?.length === 1 && (
          <Box display="flex">
            {['jpeg', 'jpg', 'png'].includes(post?.media[0]?.split('.')[post?.media[0]?.split('.').length - 1]) && (
              <Box width="100%" textAlign="center">
                <PostCardImage src={post?.media[0]} />
              </Box>
            )}
            {['mov', 'mp4'].includes(
              post?.media[0].split('.')[post?.media[0]?.split('.').length - 1]?.toLowerCase()
            ) && (
              <Box
                height="auto"
                width="100%"
                zIndex="0"
                gridArea="video"
                style={{ alignContent: 'center' }}
                // @ts-ignore
                ref={videoRef}
              >
                <PostCardVideo url={post?.media[0]} id={videoId} onPlay={onVideoPlay} />
              </Box>
            )}
          </Box>
        )}
        {Array.isArray(post?.media) && post?.media?.length > 1 && (
          <>
            {['jpeg', 'jpg', 'png'].includes(post?.media[0]?.split('.')[post?.media[0]?.split('.').length - 1]) && (
              <ElasticSlider noAutoPlay>
                {post?.media?.map(mediaEl => {
                  return (
                    <Box zIndex="0" key={post?.id + '_slider'}>
                      <PostCardImage src={mediaEl} />
                    </Box>
                  )
                })}
              </ElasticSlider>
            )}
            {['mov', 'mp4'].includes(
              post?.media[0].split('.')[post?.media[0]?.split('.').length - 1]?.toLowerCase()
            ) && (
              <Box
                height="auto"
                width="100%"
                zIndex="0"
                gridArea="video"
                // @ts-ignore
                ref={videoRef}
              >
                <PostCardVideo url={post?.media[0]} id={videoId} onPlay={onVideoPlay} />
              </Box>
            )}
          </>
        )}
      </Box>
      <Box display="flex" justifyContent="flex-end" paddingRight="0.5em">
        <PostCardReactions
          userReactions={userReactionAll}
          setShowUserReactions={setShowUserReactions}
          setActivePost={setActivePost}
          post={post}
        />
      </Box>
      <Box p="0.5em 0" display="flex" flexDirection="column">
        <Box
          display="flex"
          justifyContent={isDevice ? 'space-between' : 'space-between'}
          alignItems="center"
          borderTop={`1px solid ${theme.colors.supplementary1}`}
          padding="0.2em 0.5em"
        >
          <Box width="max-content" display="flex">
            <Box display="flex" alignItems="center">
              <Button
                design="tertiary"
                size="small"
                startIcon={<CommentLeftIcon style={{ fontSize: 20 }} />}
                onClick={() => {
                  setPage(1)
                  if (!commentsFetched) {
                    getComments(user?.id, post?.id, 1)
                    setCommentsFetched(true)
                  }
                  setShowComments(showComments ? false : true)
                }}
                style={{ whiteSpace: 'nowrap', color: theme.colors.accent1, textDecoration: 'none' }}
              >
                <Suspense fallback={<Loader />}>
                  <LazyCommentsCount count={commentCount} isDevice={isDevice} />
                </Suspense>
              </Button>
            </Box>
            <Box display="flex" alignItems="center">
              <Button
                design="tertiary"
                startIcon={<ShareIcon style={{ fontSize: 20 }} />}
                size="small"
                onClick={() => handleOpenSharePost()}
                style={{ whiteSpace: 'nowrap', color: theme.colors.accent1, textDecoration: 'none' }}
              >
                {!isDevice && (
                  <Typography component="span" variant={isDevice ? 'caption' : 'body2'}>
                    <Box color={theme.colors.accent1} fontWeight="bold">
                      Repost
                    </Box>
                  </Typography>
                )}
              </Button>
            </Box>
          </Box>
          <Box width="max-content" display="flex" alignItems="center">
            <Suspense fallback={<Loader />}>
              <LazyFAB slideSpeed={500} direction={Directions.Left} spacing={4} isOpen={isOpen}>
                <MainButton
                  iconResting={
                    userReaction?.length > 0 ? (
                      <ThumbsUpIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    ) : (
                      <ThumbsUpOutlinedIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    )
                  }
                  iconActive={<StarIcon style={{ fontSize: 20 }} />}
                  background={theme.colors.supplementary}
                  onClick={() => {
                    setIsOpen(!isOpen)
                  }}
                  size={isDevice ? 30 : 40}
                />
                <ChildButton
                  icon={
                    userReaction[0] ? (
                      <ThumbsUpIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    ) : (
                      <ThumbsUpOutlinedIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    )
                  }
                  background={theme.colors.supplementary}
                  size={isDevice ? 30 : 40}
                  onClick={() => {
                    handleReaction(0)
                    setLikes(Number(likes) + 1)
                    setIsOpen(false)
                  }}
                />
                <ChildButton
                  icon={
                    userReaction[1] ? (
                      <HeartIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    ) : (
                      <HeartOutlinedIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    )
                  }
                  background={theme.colors.supplementary}
                  size={isDevice ? 30 : 40}
                  onClick={() => {
                    handleReaction(1)
                    setLikes(Number(likes) + 1)
                    setIsOpen(false)
                  }}
                />
                <ChildButton
                  icon={
                    userReaction[2] ? (
                      <ClapIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    ) : (
                      <ClapOutlinedIcon
                        style={isDevice ? { height: '1.6em', width: '1.6em' } : { height: '2em', width: '2em' }}
                      />
                    )
                  }
                  background={theme.colors.supplementary}
                  size={isDevice ? 30 : 40}
                  onClick={() => {
                    handleReaction(2)
                    setLikes(Number(likes) + 1)
                    setIsOpen(false)
                  }}
                />
              </LazyFAB>
            </Suspense>
          </Box>
        </Box>
      </Box>
      <Box margin="0.5em" fontWeight="bold">
        {showComments && (
          <Suspense fallback={<Loader />}>
            <LazyCreateComment
              user={user}
              createComment={createComment}
              postId={post?.id}
              comments={displayComments()}
              setComments={setPostComments}
              commentCount={commentCount}
              setCommentCount={setCommentCount}
              handleComment={handleComment}
              partner={partner}
              partnerView={partnerView}
              createCommentPermission={createCommentPermission}
              theme={theme}
            />
          </Suspense>
        )}
      </Box>
      {showComments && (
        <Comments
          reportContent={reportContent}
          user={user}
          comments={displayComments()}
          setComments={setPostComments}
          postId={post?.id}
          createComment={createComment}
          commentCount={commentCount}
          setCommentCount={setCommentCount}
          deleteComment={deleteComment}
          updateComment={updateComment}
          notify={notify}
          post={post}
          getPost={getPost}
          handleScroll={handleScroll}
          backendUserFunctions={backendUserFunctions}
        />
      )}
    </StyledCard>
  )
}
export default memo(PostCard)
